import {
  BookmarkAddRounded,
  Delete,
  DragIndicator,
  Edit,
  ExpandLess,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { useParams } from "react-router-dom";
import { NoDataWarning } from "../../../components/common";
import { AuxInterventionsListItem } from "./AuxInterventionsListItem";
import { InterventionsTags } from "./InterventionsTags";
import InputText from "@/components/form/InputText";
import { useForm } from "react-hook-form";
import { SncrOpenDrawerButton } from "src-next/modules/sncr";

function SortableExamItem({
  el,
  index,
  auxIndex,
  functions,
  sortableId,
}: {
  el: any;
  index: number;
  auxIndex: number;
  functions: any;
  sortableId: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "default",
  };

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      key={`${el?.name}-${index}`}
    >
      <Stack
        p={1}
        gap={1}
        sx={{
          background: "#F1F3F9",
          borderRadius: "5px",
          width: "100%",
        }}
      >
        <Stack direction="row" gap={1}>
          <Typography variant="body1" color="neutral1000.main" fontWeight={500}>
            {index + 1}. {el?.name}
          </Typography>
          {el?.tag && <InterventionsTags type={el.tag} />}

          <Typography variant="body1" color="neutral700.main">
            Código TUSS: {el?.inputValue?.code}
          </Typography>
        </Stack>

        <Stack direction="row" gap={1}>
          <Typography variant="body1" color="neutral700.main">
            {el?.condition?.label}{" "}
            {!el?.condition?.name &&
              el.condition?.value &&
              `- CID10 ${el.condition?.value}`}
          </Typography>
        </Stack>

        <Typography variant="body1" color="neutral700.main">
          {el?.category?.label}
        </Typography>
        {el?.note && (
          <Stack direction="row" gap={1}>
            <Typography
              color="neutral1000.main"
              fontWeight={400}
              sx={{
                maxWidth: "100%",
                whiteSpace: "normal",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              Observação: {el?.note}
            </Typography>
          </Stack>
        )}
      </Stack>
      {!el?.sign && (
        <Stack direction="row" p={1} sx={{ alignItems: "center" }}>
          <Tooltip title="Arrastar para reordenar">
            <IconButton
              aria-label="Arrastar para reordenar"
              sx={{ color: "black", cursor: "grab" }}
              {...attributes}
              {...listeners}
            >
              <DragIndicator fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton sx={{ color: "black" }}>
            <Edit
              fontSize="small"
              onClick={() => functions.handleSelect(el, auxIndex)}
            />
          </IconButton>
          <IconButton
            sx={{ color: "black" }}
            onClick={() => functions.handleRemove(auxIndex)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
}

function SortableProcedureItem({
  el,
  index,
  auxIndex,
  functions,
  itemType,
}: {
  el: any;
  index: number;
  auxIndex: number;
  functions: any;
  itemType: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: el.id || `${itemType}-${auxIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "default",
  };

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      key={`${el?.name}-${index}`}
    >
      <Stack
        p={1}
        gap={1}
        sx={{
          background: "#F1F3F9",
          borderRadius: "5px",
          width: "100%",
        }}
      >
        <Stack direction="row" gap={1}>
          <Typography variant="body1" color="neutral1000.main" fontWeight={500}>
            {index + 1}. {el?.name}
          </Typography>
          {el?.tag && <InterventionsTags type={el.tag} />}
        </Stack>
        {el?.note && (
          <Stack direction="row" gap={1}>
            <Typography
              color="neutral1000.main"
              fontWeight={400}
              sx={{
                maxWidth: "100%",
                whiteSpace: "normal",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              Observação: {el?.note}
            </Typography>
          </Stack>
        )}
      </Stack>
      {!el?.sign && (
        <Stack direction="row" p={1} sx={{ alignItems: "center" }}>
          <Tooltip title="Arrastar para reordenar">
            <IconButton
              aria-label="Arrastar para reordenar"
              sx={{ color: "black", cursor: "grab" }}
              {...attributes}
              {...listeners}
            >
              <DragIndicator fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton sx={{ color: "black" }}>
            <Edit
              fontSize="small"
              onClick={() => functions.handleSelect(el, auxIndex)}
            />
          </IconButton>
          <IconButton
            sx={{ color: "black" }}
            onClick={() => functions.handleRemove(auxIndex)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
}

function SortableItem({
  el,
  index,
  auxIndex,
  functions,
  itemType,
}: {
  el: any;
  index: number;
  auxIndex: number;
  functions: any;
  itemType: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: el.id || `${itemType}-${auxIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "default",
  };

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      key={`${el?.name}-${index}`}
    >
      <Stack
        p={1}
        gap={1}
        sx={{
          background: "#F1F3F9",
          borderRadius: "5px",
          width: "100%",
        }}
      >
        <Stack direction="row" gap={1}>
          <Typography variant="body1" color="neutral1000.main" fontWeight={500}>
            {index + 1}. {el.name}
          </Typography>
          {el?.tag && <InterventionsTags type={el.tag} />}
          <Typography variant="body1" color="neutral700.main">
            {el?.inputValue?.presentation}
          </Typography>
        </Stack>

        <Stack direction="row" gap={1}>
          <Typography variant="body1" color="neutral700.main">
            {el?.form}
          </Typography>
        </Stack>
        {el?.note && (
          <Stack direction="row" gap={1}>
            <Typography
              color="neutral1000.main"
              fontWeight={400}
              sx={{
                maxWidth: "100%",
                whiteSpace: "normal",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              Observação: {el?.note}
            </Typography>
          </Stack>
        )}
      </Stack>
      {!el?.sign && (
        <Stack direction="row" p={1} sx={{ alignItems: "center" }}>
          <Tooltip title="Arrastar para reordenar">
            <IconButton
              aria-label="Arrastar para reordenar"
              sx={{ color: "black", cursor: "grab" }}
              {...attributes}
              {...listeners}
            >
              <DragIndicator fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton sx={{ color: "black" }}>
            <Edit
              fontSize="small"
              onClick={() => functions.handleSelect(el, auxIndex)}
            />
          </IconButton>
          <IconButton
            sx={{ color: "black" }}
            onClick={() => functions.handleRemove(auxIndex)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
}

export function ListaDeIntervenções({
  selectedInterventions,
  functions,
  setCreateDialog,
  expandedList,
  setExpandedList,
  appliedProtocol,
}: any) {
  const { id } = useParams();

  const methods = useForm();
  const { control } = methods;

  const medications = selectedInterventions?.all?.filter(
    (item: any) => item.type === "medication"
  );
  const supplements = selectedInterventions?.all?.filter(
    (item: any) => item.type === "supplement"
  );
  const herbalMedicines = selectedInterventions?.all?.filter(
    (item: any) => item.type === "herbalMedicine"
  );

  const questionnaires =
    selectedInterventions?.all?.filter(
      (item: any) => item.type === "questionnaire"
    ) || [];

  const hasTreatments =
    medications?.length || supplements?.length || herbalMedicines?.length;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChange = () => {
    setExpandedList((prev: boolean) => !prev);
  };

  const handleDragEndMedication = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = medications.findIndex(
        (item: any) =>
          (item.id ||
            `medication-${selectedInterventions?.all.indexOf(item)}`) ===
          active.id
      );
      const newIndex = medications.findIndex(
        (item: any) =>
          (item.id ||
            `medication-${selectedInterventions?.all.indexOf(item)}`) ===
          over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedMedications = arrayMove(medications, oldIndex, newIndex);

        const others =
          selectedInterventions?.all?.filter(
            (item: any) => item.type !== "medication"
          ) || [];

        const updatedAll = [...reorderedMedications, ...others];
        functions?.handleReorder(updatedAll);
      }
    }
  };

  const handleDragEndSupplement = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = supplements.findIndex(
        (item: any) =>
          (item.id ||
            `supplement-${selectedInterventions?.all.indexOf(item)}`) ===
          active.id
      );
      const newIndex = supplements.findIndex(
        (item: any) =>
          (item.id ||
            `supplement-${selectedInterventions?.all.indexOf(item)}`) ===
          over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedSupplements = arrayMove(supplements, oldIndex, newIndex);

        const others =
          selectedInterventions?.all?.filter(
            (item: any) => item.type !== "supplement"
          ) || [];

        const updatedAll = [
          ...medications,
          ...reorderedSupplements,
          ...others.filter((item: any) => item.type !== "medication"),
        ];
        functions?.handleReorder(updatedAll);
      }
    }
  };

  const handleDragEndHerbalMedicine = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = herbalMedicines.findIndex(
        (item: any) =>
          (item.id ||
            `herbalMedicine-${selectedInterventions?.all.indexOf(item)}`) ===
          active.id
      );
      const newIndex = herbalMedicines.findIndex(
        (item: any) =>
          (item.id ||
            `herbalMedicine-${selectedInterventions?.all.indexOf(item)}`) ===
          over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedHerbalMedicines = arrayMove(
          herbalMedicines,
          oldIndex,
          newIndex
        );

        const others =
          selectedInterventions?.all?.filter(
            (item: any) => item.type !== "herbalMedicine"
          ) || [];

        const updatedAll = [
          ...medications,
          ...supplements,
          ...reorderedHerbalMedicines,
          ...others.filter(
            (item: any) =>
              item.type !== "medication" && item.type !== "supplement"
          ),
        ];
        functions?.handleReorder(updatedAll);
      }
    }
  };

  const generalExams =
    selectedInterventions?.service?.filter(
      (el: any) => !["363679005", "108252007"].includes(el?.category?.code)
    ) || [];

  const getGeneralExamSortableId = (item: any) => {
    if (item?.id) {
      return item.id;
    }

    const allIndex = selectedInterventions?.all?.indexOf(item);
    if (typeof allIndex === "number" && allIndex !== -1) {
      return `generalExam-${allIndex}`;
    }

    const serviceIndex = selectedInterventions?.service?.indexOf(item);
    if (typeof serviceIndex === "number" && serviceIndex !== -1) {
      return `generalExam-service-${serviceIndex}`;
    }

    return `generalExam-${item?.inputValue?.code || item?.name}`;
  };

  const procedures = selectedInterventions?.procedure || [];

  const handleDragEndGeneralExam = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = generalExams.findIndex(
        (item: any) => getGeneralExamSortableId(item) === active.id
      );
      const newIndex = generalExams.findIndex(
        (item: any) => getGeneralExamSortableId(item) === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedGeneralExams = arrayMove(
          generalExams,
          oldIndex,
          newIndex
        );

        const labExams =
          selectedInterventions?.service?.filter(
            (el: any) => el?.category?.code === "108252007"
          ) || [];

        const imageExams =
          selectedInterventions?.service?.filter(
            (el: any) => el?.category?.code === "363679005"
          ) || [];

        const updatedServices = [
          ...reorderedGeneralExams,
          ...labExams,
          ...imageExams,
        ];

        const treatments =
          selectedInterventions?.all?.filter((item: any) =>
            ["medication", "supplement", "herbalMedicine"].includes(item.type)
          ) || [];

        const otherItems =
          selectedInterventions?.all?.filter(
            (item: any) =>
              item.type !== "medication" &&
              item.type !== "supplement" &&
              item.type !== "herbalMedicine" &&
              item.type !== "service"
          ) || [];

        const updatedAll = [...treatments, ...updatedServices, ...otherItems];
        functions?.handleReorder(updatedAll);
      }
    }
  };

  const handleDragEndProcedure = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = procedures.findIndex(
        (item: any) =>
          (item.id ||
            `procedure-${selectedInterventions?.all.indexOf(item)}`) ===
          active.id
      );
      const newIndex = procedures.findIndex(
        (item: any) =>
          (item.id ||
            `procedure-${selectedInterventions?.all.indexOf(item)}`) === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedProcedures = arrayMove(procedures, oldIndex, newIndex);

        const treatments =
          selectedInterventions?.all?.filter((item: any) =>
            ["medication", "supplement", "herbalMedicine"].includes(item.type)
          ) || [];

        const services = selectedInterventions?.service || [];

        const otherItems =
          selectedInterventions?.all?.filter(
            (item: any) =>
              item.type !== "medication" &&
              item.type !== "supplement" &&
              item.type !== "herbalMedicine" &&
              item.type !== "service" &&
              item.type !== "procedure"
          ) || [];

        const updatedAll = [
          ...treatments,
          ...services,
          ...reorderedProcedures,
          ...otherItems,
        ];
        functions?.handleReorder(updatedAll);
      }
    }
  };

  return (
    <Accordion
      expanded={expandedList}
      sx={{
        width: "100%",
        border: "1px solid",
        borderColor: "neutral200.main",
        borderRadius: 1,
      }}
    >
      <AccordionSummary aria-controls="panel1-content" id="panel1-header">
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
        >
          Intervenções selecionadas
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {id !== "createprotocol" ? (
              <SncrOpenDrawerButton label="SNCR" />
            ) : null}
            {selectedInterventions?.all?.length && id !== "createprotocol" ? (
              <Button
                sx={{
                  width: "fit-content",
                  textTransform: "none",
                }}
                startIcon={<BookmarkAddRounded />}
                onClick={() => setCreateDialog(true)}
              >
                Criar novo protocolo
              </Button>
            ) : (
              <></>
            )}
            <IconButton onClick={handleChange}>
              {expandedList ? <ExpandLess /> : <GridExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {appliedProtocol?.length ? (
          <Stack direction="row" spacing={2} mr={1} alignItems="center">
            <Typography
              variant="body1"
              color="neutral1000.main"
              fontWeight={500}
            >
              Protocolos aplicados:
            </Typography>
            {appliedProtocol?.map((e: any, index: number) => (
              <Chip
                key={index}
                label={e?.display}
                onDelete={() =>
                  functions.handleRemoveProtocol(index, e?.reference)
                }
                color="primary"
                style={{ margin: "4px" }}
              />
            ))}
          </Stack>
        ) : (
          <></>
        )}

        {selectedInterventions?.all.length ? (
          <>
            {hasTreatments ? (
              <Stack width="100%" spacing={1} mb={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography> Tratamentos: </Typography>
                </Stack>

                {medications?.length > 0 && (
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      color="neutral700.main"
                      fontWeight={500}
                    >
                      Medicamentos:
                    </Typography>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEndMedication}
                    >
                      <SortableContext
                        items={medications.map(
                          (el: any) =>
                            el.id ||
                            `medication-${selectedInterventions?.all.indexOf(
                              el
                            )}`
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        {medications?.map((el: any, index: number) => {
                          const auxIndex =
                            selectedInterventions?.all.indexOf(el);
                          return (
                            <SortableItem
                              key={el.id || `medication-${auxIndex}`}
                              el={el}
                              index={index}
                              auxIndex={auxIndex}
                              functions={functions}
                              itemType="medication"
                            />
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </Stack>
                )}

                {supplements?.length > 0 && (
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      color="neutral700.main"
                      fontWeight={500}
                    >
                      Suplementos:
                    </Typography>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEndSupplement}
                    >
                      <SortableContext
                        items={supplements.map(
                          (el: any) =>
                            el.id ||
                            `supplement-${selectedInterventions?.all.indexOf(
                              el
                            )}`
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        {supplements?.map((el: any, index: number) => {
                          const auxIndex =
                            selectedInterventions?.all.indexOf(el);
                          return (
                            <SortableItem
                              key={el.id || `supplement-${auxIndex}`}
                              el={el}
                              index={index}
                              auxIndex={auxIndex}
                              functions={functions}
                              itemType="supplement"
                            />
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </Stack>
                )}

                {herbalMedicines?.length > 0 && (
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      color="neutral700.main"
                      fontWeight={500}
                    >
                      Fitoterápicos:
                    </Typography>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEndHerbalMedicine}
                    >
                      <SortableContext
                        items={herbalMedicines.map(
                          (el: any) =>
                            el.id ||
                            `herbalMedicine-${selectedInterventions?.all.indexOf(
                              el
                            )}`
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        {herbalMedicines?.map((el: any, index: number) => {
                          const auxIndex =
                            selectedInterventions?.all.indexOf(el);
                          return (
                            <SortableItem
                              key={el.id || `herbalMedicine-${auxIndex}`}
                              el={el}
                              index={index}
                              auxIndex={auxIndex}
                              functions={functions}
                              itemType="herbalMedicine"
                            />
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </Stack>
                )}
              </Stack>
            ) : (
              <></>
            )}
            {selectedInterventions?.service?.length ? (
              <Stack width="100%" spacing={1} mb={1}>
                {generalExams?.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="body1" color="neutral700.main">
                      Exames Gerais
                    </Typography>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEndGeneralExam}
                    >
                      <SortableContext
                        items={generalExams.map(getGeneralExamSortableId)}
                        strategy={verticalListSortingStrategy}
                      >
                        {generalExams?.map((el: any, index: number) => {
                          const auxIndex =
                            selectedInterventions?.all.indexOf(el);
                          const sortableId = getGeneralExamSortableId(el);
                          return (
                            <SortableExamItem
                              key={sortableId}
                              el={el}
                              index={index}
                              auxIndex={auxIndex}
                              functions={functions}
                              sortableId={sortableId}
                            />
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </Stack>
                )}

                {selectedInterventions?.service?.find(
                  (el: any) => el?.category?.code === "108252007"
                ) && (
                  <Typography variant="body1" color="neutral700.main">
                    Exames Laboratoriais
                  </Typography>
                )}

                {selectedInterventions?.service
                  ?.filter((el: any) => el?.category?.code === "108252007")
                  ?.map((el: any, index: number) => {
                    return (
                      <AuxInterventionsListItem
                        el={el}
                        index={index}
                        key={`lab-${index}`}
                      />
                    );
                  })}

                {selectedInterventions?.service?.find(
                  (el: any) => el?.category?.code === "363679005"
                ) && (
                  <Typography variant="body1" color="neutral700.main">
                    Exames de Imagem
                  </Typography>
                )}
                {selectedInterventions?.service
                  ?.filter((el: any) => el?.category?.code === "363679005")
                  ?.map((el: any, index: number) => {
                    return (
                      <AuxInterventionsListItem
                        el={el}
                        index={index}
                        key={`image-${index}`}
                      />
                    );
                  })}
              </Stack>
            ) : (
              <></>
            )}
            {procedures?.length ? (
              <Stack width="100%" spacing={1} mb={1}>
                <Typography> Procedimentos: </Typography>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEndProcedure}
                >
                  <SortableContext
                    items={procedures.map(
                      (el: any) =>
                        el.id ||
                        `procedure-${selectedInterventions?.all.indexOf(el)}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    {procedures?.map((el: any, index: number) => {
                      const auxIndex = selectedInterventions?.all.indexOf(el);
                      return (
                        <SortableProcedureItem
                          key={el.id || `procedure-${auxIndex}`}
                          el={el}
                          index={index}
                          auxIndex={auxIndex}
                          functions={functions}
                          itemType="procedure"
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </Stack>
            ) : (
              <></>
            )}
            {questionnaires?.length ? (
              <Stack width="100%" spacing={1} mb={1}>
                <Typography> Questionários: </Typography>
                {questionnaires?.map((el: any, index: number) => {
                  const auxIndex = selectedInterventions?.all?.indexOf(el);

                  return (
                    <Stack direction="row" key={el?.questionnaireId || index}>
                      <Stack
                        p={1}
                        gap={1}
                        sx={{
                          background: "#F1F3F9",
                          borderRadius: "5px",
                          width: "100%",
                        }}
                      >
                        <Stack direction="row" gap={1} alignItems="center">
                          <Typography
                            variant="body1"
                            color="neutral1000.main"
                            fontWeight={500}
                          >
                            {index + 1}. {el?.title || el?.name || "-"}
                          </Typography>
                        </Stack>
                        {el?.usageType && (
                          <Typography variant="body2" color="neutral700.main">
                            Categoria:{" "}
                            {el?.usageType === "triage"
                              ? "Triagem"
                              : el?.usageType === "preConsultation"
                                ? "Pré-consulta"
                                : el?.usageType === "postConsultation"
                                  ? "Pós-consulta"
                                  : "Acompanhamento"}
                          </Typography>
                        )}
                      </Stack>

                      <Stack direction="row" p={1} sx={{ alignItems: "center" }}>
                        <IconButton
                          sx={{ color: "black" }}
                          onClick={() => functions.handleRemove(auxIndex)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <></>
            )}
            {selectedInterventions?.certificates?.length ? (
              <Stack width="100%" spacing={1} mb={1}>
                <Typography> Relatórios: </Typography>
                {selectedInterventions?.certificates?.map(
                  (el: any, index: number) => {
                    const auxIndex = selectedInterventions?.all?.indexOf(el);
                    return (
                      <Stack direction="row" key={`${el?.name}-${index}`}>
                        <Stack
                          p={1}
                          gap={1}
                          sx={{
                            background: "#F1F3F9",
                            borderRadius: "5px",
                            width: "100%",
                          }}
                        >
                          <Stack direction="row" gap={1}>
                            <Typography
                              variant="body1"
                              color="neutral1000.main"
                              fontWeight={500}
                            >
                              {index + 1}. {el?.name}
                            </Typography>
                            {el?.tag && <InterventionsTags type={el.tag} />}
                          </Stack>

                          {el?.note && (
                            <InputText
                              name="report"
                              control={control}
                              disabled
                              multiline
                              minRows={4}
                              maxRows={100}
                              label=""
                              value={el.note}
                            />
                          )}
                        </Stack>

                        {!el?.sign && (
                          <Stack
                            direction="row"
                            p={1}
                            sx={{ alignItems: "center" }}
                          >
                            <IconButton sx={{ color: "black" }}>
                              <Edit
                                fontSize="small"
                                onClick={() =>
                                  functions.handleSelect(el, auxIndex)
                                }
                              />
                            </IconButton>
                            <IconButton
                              sx={{ color: "black" }}
                              onClick={() => functions.handleRemove(auxIndex)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                      </Stack>
                    );
                  }
                )}
              </Stack>
            ) : (
              <></>
            )}
          </>
        ) : (
          <NoDataWarning message="Nenhuma intervenção foi selecionada" />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
